import React from 'react';
import PropTypes from 'prop-types';
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import cx from 'classnames';

import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

import { InlineComponent, ListComponent, HistoryComponent } from './ToolBarButtons';
import LinkComponent from './LinkToolBarButtons'
import scrubHtml from './utils/scrubHtml';
import Label from '../Label';

export default class RichTextField extends React.PureComponent {
    static propTypes = {
        /**  html id */
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,

        /** Custom class added to the outer div */
        className: PropTypes.string,

        /**  Contents of the placeholder */
        placeholder: PropTypes.string,

        /** Pre-populates the field with the passed html */
        html: PropTypes.string,

        /**  Function fired with the field contents change. Passes the field contents as html and the id as parameters */
        onChange: PropTypes.func,

        /**  Function fired with the field is blurred. Passes the field contents as html and the id as parameters */
        onBlur: PropTypes.func,

        /**  Function fired with the field is focused. Passes the id as a parameter */
        onFocus: PropTypes.func,

        /** Marks and styles the field as invalid */
        isInvalid: PropTypes.bool,

        /** Contents of the error message, usually text */
        errorText: PropTypes.node,

        /** Label contents, usually text */
        label: PropTypes.node.isRequired,

        /** If false, the label will be screen reader only */
        isLabelVisible: PropTypes.bool,

        /** Marks the field as required; Adds required styling to label */
        isRequired: PropTypes.bool,

        /** Show the bold button */
        hasBoldButton: PropTypes.bool,

        /** Show the italics button */
        hasItalicsButton: PropTypes.bool,

        /** Show the unordered list button */
        hasUnorderedListButton: PropTypes.bool,

        /** Show the ordered list button */
        hasOrderedListButton: PropTypes.bool,

        /** Show the link/unlink button */
        hasLinkButton: PropTypes.bool,

        /** If the component should convert the editor state to html on each change.
        Depending on the size of the content, this could hinder performance. If false,
        the html is parse only onBlur. */
        shouldParseHtmlOnChange: PropTypes.bool,

        /** Focuses the input when it mounts */
        shouldFocusOnMount: PropTypes.bool,

        /** Run Spell Check for the editor. In IE this is always disabled. In Safari using this will also enable
        autocorrect if the user enabled it. */
        useSpellCheck: PropTypes.bool,

        /** Component is disabled (using draft-js's readOnly) */
        isDisabled: PropTypes.bool
    };

    static defaultProps = {
        html: '',
        onChange: undefined,
        className: undefined,
        placeholder: undefined,
        isInvalid: false,
        errorText: undefined,
        isLabelVisible: true,
        isRequired: false,
        onBlur: undefined,
        onFocus: undefined,
        shouldParseHtmlOnChange: true,
        hasBoldButton: true,
        hasItalicsButton: false,
        hasUnorderedListButton: true,
        hasOrderedListButton: false,
        hasLinkButton: true,
        shouldFocusOnMount: false,
        useSpellCheck: false,
        isDisabled: false
    };

    constructor(props) {
        super(props);

        this.state = this.getIncomingState(props.html);
        this.state.isFocused = false;
    }

    componentDidMount() {
        const { shouldFocusOnMount } = this.props;

        if (shouldFocusOnMount && this.editorRef) {
            this.timeout = setTimeout(() => {
                this.editorRef.focus();
            }, 0);
        }
    }

    componentDidUpdate(prevProps) {
        const { html } = this.props;

        if (html !== prevProps.html && !this.state.isFocused) {
            const newState = this.getIncomingState(html);

            this.setState(newState);
        }
    }

    componentWillUnmount() {
        clearTimeout(this.timeout);
    }

    getIncomingState(html) {
        let parsedHtml;
        let editorState;
        let hasInternalError = false;
        let internalErrorText;

        try {
            const scrubbedHtml = scrubHtml(html);
            editorState = this.getEditorStateFromHtml(scrubbedHtml);
        }
        catch (err) {
            hasInternalError = true;
            internalErrorText = "Error reading incoming html.";
        }

        if (editorState) {
            parsedHtml = this.parseEditorStateHtmlToString(editorState);
        }

        return {
            editorState,
            parsedHtml,
            hasInternalError,
            internalErrorText
        };
    }

    setEditorRef = (ref) => {
        this.editorRef = ref;
    }

    getEditorStateFromHtml(html) {
        // The editor doesn't deal in html directly, so any incoming html must be converted
        const contentBlock = htmlToDraft(html);
        let editorState;

        if (contentBlock) {
            const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
            editorState = EditorState.createWithContent(contentState);
        }
        else {
            editorState = EditorState.createEmpty();
        }
        return editorState;
    }

    handleEditorBlur = () => {
        // Because this isn't a native input, we're using state to set classes to style
        // the focus state and determine when not to update from props that would move the cursor around
        const { onBlur, id } = this.props;
        const { parsedHtml } = this.state;

        this.setState({
            isFocused: false
        });

        if (onBlur) {
            onBlur(parsedHtml, id);
        }
    }

    handleEditorFocus = () => {
        // Because this isn't a native input, we're using state to set classes to style
        // the focus state and determine when not to update from props that would move the cursor around
        const { onFocus, id } = this.props;

        this.setState({
            isFocused: true
        });

        if (onFocus) {
            onFocus(id);
        }
    }

    parseEditorStateHtmlToString(editorState) {
        return scrubHtml(draftToHtml(convertToRaw(editorState.getCurrentContent())));
    }

    handleEditorStateChange = (editorState) => {
        const { onChange, id, shouldParseHtmlOnChange } = this.props;

        if (shouldParseHtmlOnChange) {
            try {
                const parsedHtml = this.parseEditorStateHtmlToString(editorState);

                this.setState({
                    editorState,
                    parsedHtml,
                    hasInternalError: false,
                    internalErrorText: undefined
                });

                if (onChange) {
                    onChange(parsedHtml, id);
                }
            }
            catch (err) {
                this.setState({
                    editorState,
                    hasInternalError: true,
                    internalErrorText: 'Error converting contents to html.'
                });
            }
        }
        else {
            this.setState({
                editorState,
                hasInternalError: false,
                internalErrorText: undefined
            });
        }
    };

    renderErrorMessage = () => {
        const { errorText, id } = this.props;
        const { internalErrorText } = this.state;

        if (errorText || internalErrorText) {
            return (
                <div
                    id={`${id}-error`}
                    className="sarsa-field-error-message">
                    <span className="req" aria-hidden="true">*</span>
                    {errorText}
                    {internalErrorText}
                </div>
            );
        }
        return null;
    }

    render() {
        const {
            id,
            className,
            placeholder,
            isInvalid,
            label,
            isLabelVisible,
            isRequired,
            hasBoldButton,
            hasItalicsButton,
            hasUnorderedListButton,
            hasOrderedListButton,
            hasLinkButton
        } = this.props;
        const { editorState, hasInternalError } = this.state;

        const toolbarOptions = [];

        if (hasBoldButton || hasItalicsButton) {
            toolbarOptions.push('inline');
        }
        if (hasOrderedListButton || hasUnorderedListButton) {
            toolbarOptions.push('list');
        }
        if (hasLinkButton) {
            toolbarOptions.push('link');
        }
        toolbarOptions.push('history');

        const inlineOptions = [];
        if (hasBoldButton) {
            inlineOptions.push('bold');
        }
        if (hasItalicsButton) {
            inlineOptions.push('italic');
        }

        const listOptions = [];
        if (hasUnorderedListButton) {
            listOptions.push('unordered');
        }
        if (hasOrderedListButton) {
            listOptions.push('ordered');
        }


        return (
            <div
                data-component="RichTextField"
                className={cx('sarsa-field-outer-container', className)}>
                <Label
                    htmlFor={id}
                    isVisible={isLabelVisible}
                    isRequired={isRequired}>
                    {label}
                </Label>
                <div
                    className={cx('sarsa-field-input-wrapper', {
                        'no-label': !isLabelVisible
                    })}>
                    <div
                        aria-label={`Rich text editor for ${label}`}
                        className={cx('sarsa-rich-text-field', {
                            'is-focused': this.state.isFocused,
                            'is-invalid': isInvalid || hasInternalError,
                            'is-disabled': this.props.isDisabled
                        })}>
                        <Editor
                            editorRef={this.setEditorRef}
                            placeholder={placeholder}
                            id={id}
                            wrapperClassName="sarsa-rich-text-field-wrapper"
                            stripPastedStyles
                            editorClassName="sarsa-rich-text-field-editor"
                            editorState={editorState}
                            onEditorStateChange={this.handleEditorStateChange}
                            onFocus={this.handleEditorFocus}
                            onBlur={this.handleEditorBlur}
                            readOnly={this.props.isDisabled}
                            spellCheck={this.props.useSpellCheck}
                            toolbar={{
                                options: toolbarOptions,
                                inline: {
                                    inDropdown: false,
                                    component: InlineComponent,
                                    options: inlineOptions
                                },
                                link: {
                                    component: LinkComponent,
                                    showOpenOptionOnHover: false
                                },
                                list: {
                                    inDropdown: false,
                                    component: ListComponent,
                                    options: listOptions
                                },
                                history: {
                                    inDropdown: false,
                                    component: HistoryComponent
                                }
                            }}
                            ariaDescribedBy={`${id}-label`} />
                    </div>
                    {this.renderErrorMessage()}
                </div>
            </div>
        );
    }
}
